/** Arbitrary structured data attached to one log entry. */
export type LogContext = Record<string, unknown>;

export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

/** An observability backend the logger forwards to, alongside its console output. */
export type LogSink = (level: LogLevel, message: string, context?: LogContext) => void;

// Next compiles this module into several server chunks per build (the
// instrumentation graph and each SSR graph get their own), so a module-scope
// binding would leave the sink registered in one copy and absent from the rest.
// The registry hangs off the realm instead, keyed by a cross-copy `Symbol.for`,
// which is the same carrier trick the Sentry SDK uses for its own client.
const LOG_SINK_KEY = Symbol.for('@jaaq/observability.registeredLogSink');

type LogSinkCarrier = { [LOG_SINK_KEY]?: LogSink | null };

const carrier = globalThis as LogSinkCarrier;

/**
 * Point the logger at an observability backend, or `null` to detach it again.
 *
 * The dependency is inverted deliberately. Around 25 packages depend on
 * @jaaq/observability, so a backend SDK declared here would become transitive
 * for every one of them – client bundles included. Instead each app registers a
 * sink during its own instrumentation, and this package keeps its two
 * dependencies.
 */
export const registerLogSink = (sink: LogSink | null): void => {
  carrier[LOG_SINK_KEY] = sink;
};

/**
 * Package-internal: hands one entry to the registered sink, if any. A sink that
 * throws is swallowed rather than logged – reporting it would re-enter the
 * logger, and a broken backend must not take a caller's code path down with it.
 */
export const emitToLogSink = (level: LogLevel, message: string, context?: LogContext): void => {
  const sink = carrier[LOG_SINK_KEY];
  if (!sink) {
    return;
  }
  try {
    sink(level, message, context);
  } catch {
    // Deliberately silent; see above.
  }
};
